import type { ElementKey } from '@/lib/experience'
import { ELEMENTS } from '@/lib/experience'
import ProtocolWhatsappTestimonials from '@/components/ProtocolWhatsappTestimonials'
import { motion } from 'framer-motion'

interface Props {
  element: ElementKey
  onRestart: () => void
  onProtocol: () => void
}

const JORNADA_COMPLETA_LINK = 'https://pay.hotmart.com/O105657366F?off=x4c01y5z'

const CYCLES = [
  { emoji: '🌱', title: 'Raízes', desc: 'Liberação de programações familiares e crenças herdadas.' },
  { emoji: '🌿', title: 'Padrões', desc: 'Interrupção de loops de autossabotagem e repetição.' },
  { emoji: '🌾', title: 'Feridas', desc: 'Liberação do não resolvido e ressignificação de memórias.' },
  { emoji: '🍃', title: 'Reconexão', desc: 'Liberte-se da identidade que o passado construiu e reconecte com quem você veio ser.' },
  { emoji: '🌸', title: 'Merecimento', desc: 'Dissolva a crença de que não merece e ancore uma nova realidade de plenitude e abundância.' },
  { emoji: '🍎', title: 'Crescimento', desc: 'Sustente sua transformação, expanda em todas as áreas e floresça de forma permanente.' },
]

const FAQ_ITEMS = [
  {
    q: 'Como vou receber a meditação e tudo que contempla cada ciclo?',
    a: 'Tudo é entregue por e-mail: o Mapa de Revelações, a Trindade de Cura, os Decretos Eu Sou e o link da sua meditação personalizada.',
  },
  {
    q: 'Qual o prazo de entrega após o pagamento?',
    a: 'De 3 a 5 dias úteis após a confirmação do pagamento e o envio do formulário preenchido.',
  },
  {
    q: 'Como vou ter acesso ao formulário?',
    a: 'Após a confirmação do pagamento, o link do formulário será enviado diretamente para o seu WhatsApp.',
  },
  {
    q: 'Terei suporte durante o processo?',
    a: 'Sim. Você pode enviar dúvidas por e-mail ou diretamente pelo WhatsApp de Mônica Maria durante todo o ciclo.',
  },
  {
    q: 'Preciso ter algum conhecimento prévio em meditação ou espiritualidade?',
    a: 'Não. O Protocolo foi criado para funcionar independentemente do nível de conhecimento. Tudo que você precisa é de fones de ouvido e disposição para escutar a meditação antes de dormir.',
  },
  {
    q: 'E se eu dormir no meio da meditação?',
    a: 'Isso é completamente natural e faz parte do processo. O estado de sonolência favorece a liberação porque as resistências do consciente se baixam. Quer você escute acordado ou dormindo, a transformação acontece.',
  },
  {
    q: 'Posso escutar a meditação mais de uma vez?',
    a: 'Pode, desde que preferencialmente deite e fique em estado relaxado. O recomendado é escutar no horário em que for dormir, pois tudo que foi escutado vai continuar reverberando durante toda a noite.',
  },
  {
    q: 'A meditação é gerada por inteligência artificial?',
    a: 'Não. Cada meditação é gravada individualmente por Mônica Maria com presença e intenção direcionadas especificamente para o seu campo vibracional. É a voz, a energia e a conexão dela com o que o seu campo revelou.',
  },
  {
    q: 'Posso fazer o Protocolo se já faço outras terapias ou práticas espirituais?',
    a: 'Sim. O Protocolo complementa qualquer processo que você já esteja fazendo. Na verdade, muitas pessoas que chegam já percorreram vários caminhos de cura e encontram no Protocolo o que ainda estava faltando: a liberação da raiz interna que mantém os padrões limitantes.',
  },
]

function Divider({ accent }: { accent: string }) {
  return (
    <div
      className="h-px w-full my-12 md:my-16"
      style={{ background: `linear-gradient(90deg, transparent, ${accent}55, transparent)` }}
    />
  )
}

function StepOffers({ element, onRestart, onProtocol }: Props) {
  const meta = ELEMENTS[element]
  const accent = meta.accent
  const accentSoft = meta.accentSoft

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-3xl mx-auto px-5 md:px-0"
    >

      {/* ── BLOCO 1 — HEADLINE ── */}
      <div className="text-center mb-12 md:mb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="uppercase font-body mb-5"
          style={{ fontSize: '1rem', letterSpacing: '0.4em', lineHeight: 1.7, color: `${accentSoft}a8` }}
        >
          Protocolo de Liberação e Cocriação
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="font-display text-[var(--ink)]"
          style={{
            fontSize: 'clamp(1.9rem, 5vw, 2.8rem)',
            fontWeight: 600,
            lineHeight: 1.12,
            color: accentSoft,
          }}
        >
          Libere o que te limita e acesse a vida que você merece.
        </motion.h1>
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 80, opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.45 }}
          className="h-px mx-auto mt-8"
          style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        />
      </div>

      {/* ── BLOCO 2 — IDENTIFICAÇÃO DA DOR ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <p
          className="font-body mb-8 text-center"
          style={{ fontSize: 'clamp(1.25rem, 2.8vw, 1.45rem)', fontWeight: 600, lineHeight: 1.5, color: 'var(--ink)' }}
        >
          Esses são os <span className="numerals">3</span> maiores motivos de você viver ciclos repetitivos e negativos:
        </p>
        <div className="space-y-5">
          {[
            'Você está parado no tempo e preso em pensamentos, dores, crenças, frases ou memórias negativas.',
            'Você não sabe equilibrar a mente em situações de caos, bloqueios e desequilíbrios.',
            'Você fica cada vez mais emaranhado, limitado e tudo se repete enquanto não romper com o que já passou, mas continua vivo e vibrando dentro de você.',
          ].map((text, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-sm p-5 sm:p-6"
              style={{
                background: `linear-gradient(135deg, ${accent}12 0%, ${accent}04 100%)`,
                border: `1px solid ${accent}33`,
              }}
            >
              <span style={{ color: accent, fontSize: '1.1rem', flexShrink: 0, marginTop: '2px' }}>✦</span>
              <p className="font-body" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.85)' }}>
                {text}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      <Divider accent={accent} />

      {/* ── BLOCO 3 — AUTORIDADE ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.35 }}
        className="mb-12 md:mb-16"
      >
        <div className="flex justify-center mb-8">
          <div
            className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden"
            style={{
              border: `2px solid ${accentSoft}66`,
              boxShadow: `0 0 24px ${accent}22`,
            }}
          >
            <img
              src="https://static.lumi.new/66/66a59164029fe3389411bfef927c8644.webp"
              alt="Mônica Maria — Terapeuta Vibracional"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="text-center mb-8">
          <p
            className="font-display mb-1"
            style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.9rem)', fontWeight: 600, lineHeight: 1.2, color: 'var(--ink)' }}
          >
            Mônica Maria
          </p>
          <p
            className="font-body"
            style={{ fontSize: 'clamp(1.05rem, 2.2vw, 1.15rem)', fontWeight: 400, lineHeight: 1.6, color: `${accentSoft}b3`, fontStyle: 'italic' }}
          >
            Terapeuta Vibracional e Especialista em Leitura do Campo Vibracional
          </p>
        </div>

        <div className="space-y-5 max-w-2xl mx-auto">
          {[
            'Desde 2017 conduzo pessoas que já percorreram muitos caminhos de cura, terapia, constelação, coaching e práticas espirituais, e que ainda carregam padrões repetitivos e limitantes.',
            'São quase 10 anos, mais de 15 cursos lançados e milhares de pessoas transformadas.',
            'Meu trabalho é revelar o que está invisível e liberar o que ainda prende.',
            'O Protocolo de Liberação e Cocriação é conduzido através da Trindade de Cura: uma metodologia criada exclusivamente por mim para liberar dores e elevar a vibração, para que você crie a vida que merece viver.',
          ].map((text, i) => (
            <p
              key={i}
              className="font-body"
              style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.78)', textAlign: 'center' }}
            >
              {text}
            </p>
          ))}
        </div>
      </motion.div>

      <Divider accent={accent} />

      {/* ── BLOCO 5 — O QUE É O PROTOCOLO ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <h2
          className="font-display text-center mb-10"
          style={{ fontSize: 'clamp(1.6rem, 4vw, 2rem)', fontWeight: 600, lineHeight: 1.18, color: accentSoft }}
        >
          O que é o Protocolo de Liberação e Cocriação
        </h2>

        <div className="space-y-5 max-w-2xl mx-auto">
          {[
            'Eu consigo facilmente fazer a leitura do campo vibracional das pessoas à distância. Sinto angústia, pressa, desespero, dúvida, medo e padrões dos mais diversos tipos.',
            'Quero lhe dizer hoje que a sua mudança pode ser mais rápida e sem que haja inúmeros atendimentos e horas que muitas vezes são necessários investir em tempo, dinheiro e disposição.',
            'Eu lhe mostro sua dor, a vibração negativa que está lhe prendendo, as frases que limitam sua evolução, quem você ainda não perdoou, quem lhe prende de forma consciente ou inconsciente, e lhe apresento uma forma simples de liberar tudo que lhe limita para que você viva a vida que você merece.',
            'Não é mágica. É mudança de vibração.',
            'É lhe mostrar como abraçar sua dor e fazer com que ela seja liberada. Se trata de rasgar o véu que cobre sua percepção de você mesmo. Você nem tem ideia de quanto há frases, limites e dores que podem sair de forma simples.',
            'O que mais acontece é que muitas pessoas já estão totalmente mergulhadas nesse universo de autoconhecimento e expansão da consciência, mas não conseguem realmente mudar de vida. Praticam orações, decretos, fazem afirmações, práticas e até terapias, mas a raiz interna não é modificada e, de alguma forma, os padrões limitantes voltam a fazer parte da vida.',
            'O Protocolo de Liberação e Cocriação atua fazendo uma investigação minuciosa em dores que estão escondidas internamente dentro de você. O que é atrativo é que cada ciclo contempla questões específicas e cirúrgicas que vão apontar padrões enraizados a partir de você e que, se você fosse responder tudo isso pessoalmente em um atendimento, precisaria de horas.',
            'Outro ponto fundamental é que quando você preenche o formulário, isso por si só faz você iniciar sua jornada corajosa de mudança, pois você traz à tona situações aparentemente esquecidas, mas que estão vivas ou reprimidas dentro de você, impactando totalmente a sua vibração, a sua vida e o seu crescimento.',
            'Faço isso através de 6 ciclos de 21 dias. Não é necessário ficar lhe escutando por várias horas em atendimentos. Tudo que você faz é preencher um formulário detalhando situações passadas que estão em suas memórias.',
            'A meditação é criada baseada totalmente no que você preencheu no formulário. Frases que parecem inofensivas e memórias marcantes revelam padrões negativos que operam muitas vezes de forma inconsciente, limitando seu crescimento.',
            'Quando leio, mapeio suas dores e lhe apresento por escrito o que está limitando sua vida e como resolver, com um Mapa de Revelações: um documento personalizado com os padrões do seu campo vibracional e o caminho de liberação.',
            'Além disso, gravo de forma individual e personalizada uma meditação com duração média de até 30 minutos que você vai escutar na hora em que for dormir.',
            'Não é uma mera meditação que você encontra em canais e na internet. É uma meditação com uma sequência de cura, baseada na metodologia da Trindade de Cura, para liberar, elevar e lhe impulsionar para a plenitude em todas as áreas da vida.',
          ].map((text, i) => (
            <p
              key={i}
              className="font-body"
              style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.85, color: 'rgba(232,224,213,0.78)' }}
            >
              {text}
            </p>
          ))}
        </div>

        <p
          className="font-body mt-8 mb-6 max-w-2xl mx-auto"
          style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 500, lineHeight: 1.7, color: 'var(--ink)' }}
        >
          A meditação segue as três etapas da Trindade de Cura:
        </p>

        <div className="space-y-5 max-w-2xl mx-auto">
          {[
            {
              label: 'Reconhecer',
              text: 'quando você escuta, automaticamente percebe seus limites, dores e crenças. Muitas pessoas choram e se emocionam profundamente. O que faço é trazer uma forma de você acessar a dor enraizada dentro de você de forma inconsciente.',
            },
            {
              label: 'Liberar',
              text: 'após reconhecer a dor, integro e lhe apresento comandos individuais e personalizados para liberação, alívio e elevação.',
            },
            {
              label: 'Reprogramar',
              text: 'a meditação tem uma etapa de reprogramação mental que cria portas, estímulos e caminhos para que a sua vida seja diferente a partir do primeiro momento em que escuta.',
            },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-sm p-5 sm:p-6"
              style={{
                background: `linear-gradient(135deg, ${accent}12 0%, ${accent}04 100%)`,
                border: `1px solid ${accent}33`,
              }}
            >
              <span style={{ color: accent, fontSize: '1.1rem', flexShrink: 0, marginTop: '3px' }}>✦</span>
              <p className="font-body" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.85)' }}>
                <strong style={{ color: accentSoft, fontWeight: 600 }}>{item.label}:</strong>{' '}
                {item.text}
              </p>
            </div>
          ))}
        </div>

        <div className="space-y-5 max-w-2xl mx-auto mt-8">
          {[
            'A meditação é feita para você escutar no horário em que vai dormir. O que é incrível é que nesse estado de sonolência e relaxamento as resistências do seu consciente se baixam, e isso favorece totalmente a liberação, a reprogramação e a elevação do seu estado interno.',
            'É natural você começar a escutar e entrar em relaxamento à medida que a meditação avança. Diversas pessoas que já fazem o Protocolo de Liberação e Cocriação relatam que dormem melhor. Por isso, quer você escute acordado ou dormindo, a meditação fará uma grande transformação.',
            'O que já foi relatado por várias pessoas que fazem esse processo é que surgem lembranças ao acordar e ao longo do dia de fatos e memórias que nem se lembravam. Ou seja, a verdade vai sendo vista e você percebe que havia tanta coisa negativa vibrando a partir de você.',
            'Outro ponto crucial: quando você faz um processo terapêutico, muitas vezes fala uma única vez sobre uma dor, trauma ou circunstância do passado. Na meditação que você escutará durante os 21 dias é como se você, a cada ciclo, estivesse passando gradualmente por um terapeuta que sabe todas as dores que permeiam sua vida. E toda vez que você escuta a meditação, isso traz mais expressão, cura e liberação.',
            'Automaticamente ao final dos 21 dias você é um novo ser, com uma nova consciência, vibração e vida.',
            'O que é único e poderoso é que a meditação é gravada direcionada para o seu campo vibracional. Isso por si carrega frequências de cura, equilíbrio e crescimento. É a minha voz, a minha energia e a minha conexão com o que o seu campo revelou, criada para você escutar todas as noites por 21 dias.',
          ].map((text, i) => (
            <p
              key={i}
              className="font-body"
              style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.85, color: 'rgba(232,224,213,0.78)' }}
            >
              {text}
            </p>
          ))}
        </div>

        {/* Destaque — Não é IA */}
        <div
          className="relative rounded-sm p-6 sm:p-7 mt-8 max-w-2xl mx-auto"
          style={{
            background: `linear-gradient(135deg, ${accent}16 0%, ${accent}06 100%)`,
            border: `1px solid ${accentSoft}59`,
          }}
        >
          <div
            className="absolute left-0 inset-y-0 w-[3px] rounded-l-sm"
            style={{ background: `linear-gradient(180deg, ${accentSoft}, ${accent}, transparent)` }}
          />
          <p
            className="uppercase font-body mb-3"
            style={{ fontSize: '0.78rem', letterSpacing: '0.3em', color: accentSoft, lineHeight: 1.6 }}
          >
            ✦ Gravado com presença real
          </p>
          <p
            className="font-body"
            style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'var(--ink)' }}
          >
            Não é inteligência artificial. É presença real.
          </p>
        </div>
      </motion.div>

      <Divider accent={accent} />

      {/* ── BLOCO 6 — OS 6 CICLOS ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <div className="text-center mb-10">
          <h2
            className="font-display mb-4"
            style={{ fontSize: 'clamp(1.7rem, 4vw, 2.1rem)', fontWeight: 600, lineHeight: 1.15, color: accentSoft }}
          >
            Um processo que cresce com você.
          </h2>
          <p
            className="font-body max-w-xl mx-auto"
            style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.72)' }}
          >
            Cada ciclo de <span className="numerals">21</span> dias dissolve uma camada e prepara o terreno para a próxima. Você começa onde está. E avança no seu tempo.
          </p>
        </div>

        <div className="space-y-4">
          {CYCLES.map((cycle, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-sm p-5 sm:p-6"
              style={{
                background: `linear-gradient(135deg, ${accent}0f 0%, ${accent}04 100%)`,
                border: `1px solid ${accent}2e`,
              }}
            >
              <span style={{ fontSize: '1.5rem', flexShrink: 0, lineHeight: 1.4 }}>{cycle.emoji}</span>
              <div>
                <p
                  className="font-display mb-1"
                  style={{ fontSize: 'clamp(1.2rem, 2.8vw, 1.4rem)', fontWeight: 500, lineHeight: 1.3, color: accentSoft }}
                >
                  Ciclo <span className="numerals">{i + 1}</span>{' '}
                  <em className="italic font-light">— {cycle.title}</em>
                </p>
                <p
                  className="font-body"
                  style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', fontWeight: 400, lineHeight: 1.75, color: 'rgba(232,224,213,0.7)' }}
                >
                  {cycle.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      <Divider accent={accent} />

      {/* ── BLOCO 7 — DEPOIMENTOS ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <h2
          className="font-display text-center mb-10"
          style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.9rem)', fontWeight: 600, lineHeight: 1.2, color: accentSoft }}
        >
          O que estão dizendo sobre o Protocolo de Liberação e Cocriação
        </h2>
        <ProtocolWhatsappTestimonials accentSoft={accentSoft} />
      </motion.div>

      <Divider accent={accent} />

      {/* ── BLOCO 8 — OFERTA ── */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <div className="flex flex-col gap-6">

          {/* Jornada Completa */}
          <div
            className="rounded-sm p-6 sm:p-8 md:p-10 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(200,146,58,0.08) 0%, rgba(200,146,58,0.02) 100%)',
              border: '1px solid rgba(200,146,58,0.4)',
            }}
          >
            <div
              className="absolute inset-x-0 -top-24 h-48 pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, rgba(200,146,58,0.14), transparent)' }}
            />

            <div className="flex items-center gap-3 mb-6">
              <span
                className="uppercase font-body px-3 py-1 rounded-full"
                style={{ fontSize: '0.82rem', letterSpacing: '0.3em', color: '#E8C77A', border: '1px solid rgba(200,146,58,0.45)', background: 'rgba(200,146,58,0.10)', lineHeight: 1.7 }}
              >
                Comece sua jornada de transformação.
              </span>
            </div>

            <h3
              className="font-display mb-4"
              style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.85rem)', fontWeight: 500, lineHeight: 1.18, color: '#E8C77A' }}
            >
              Jornada Completa —{' '}
              <em className="italic font-light"><span className="numerals">6</span> Ciclos</em>
            </h3>

            <ul className="space-y-3 mb-5">
              {[
                'Diagnóstico do elemento mais bloqueado',
                'Mapa de Revelações personalizado',
                'Trindade de Cura personalizada',
                'Decretos Eu Sou de cada ciclo',
                'Meditação gravada com presença real para 21 noites',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="flex-shrink-0" style={{ color: '#E8C77A', fontSize: '1.1rem', lineHeight: 1.6 }}>✦</span>
                  <span className="font-body" style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', lineHeight: 1.7, color: 'rgba(232,224,213,0.8)' }}>
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <p
              className="font-body mb-6"
              style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.78)' }}
            >
              <span className="numerals">6</span> ciclos progressivos de <span className="numerals">21</span> dias. São <span className="numerals">126</span> dias de transformação camada por camada.
            </p>

            <div className="pt-6 border-t" style={{ borderColor: 'rgba(200,146,58,0.25)' }}>
              <p
                className="font-body mb-2"
                style={{ fontSize: 'clamp(1.35rem, 3.2vw, 1.7rem)', fontWeight: 600, lineHeight: 1.3, color: 'var(--ink)' }}
              >
                De{' '}
                <span className="numerals" style={{ textDecoration: 'line-through', color: 'rgba(232,224,213,0.45)', fontWeight: 400 }}>R$1.782</span>
                {' '}por{' '}
                <span className="numerals">R$997</span>
                {' '}à vista
              </p>
              <p
                className="font-body mb-6"
                style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.4, color: 'rgba(232,224,213,0.55)' }}
              >
                ou <span className="numerals">12x</span> de <span className="numerals">R$103,11</span>
              </p>
              <a
                href={JORNADA_COMPLETA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center rounded-xl uppercase transition-all duration-300 hover:brightness-110 hover:-translate-y-[1px] active:translate-y-0"
                style={{
                  fontSize: 'clamp(1.2rem, 2.6vw, 1.35rem)',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  padding: '20px 36px',
                  minHeight: '60px',
                  lineHeight: 1.5,
                  background: '#C8923A',
                  color: '#ffffff',
                  boxShadow: '0 6px 18px rgba(200,146,58,0.28), 0 1px 2px rgba(0,0,0,0.18)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                Quero a Jornada Completa
              </a>
              <p
                className="font-body text-center mt-4"
                style={{ fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.6, color: 'rgba(232,224,213,0.55)', fontStyle: 'italic' }}
              >
                Os ciclos são entregues progressivamente, um a cada <span className="numerals">21</span> dias conforme você avança na jornada.
              </p>
            </div>
          </div>

        </div>
      </motion.div>

      <Divider accent={accent} />

      {/* ── BLOCO 9 — FAQ ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-16"
      >
        <h2
          className="font-display text-center mb-10"
          style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.9rem)', fontWeight: 600, lineHeight: 1.2, color: accentSoft }}
        >
          Perguntas frequentes
        </h2>
        <div className="space-y-4">
          {FAQ_ITEMS.map((item, i) => (
            <div
              key={i}
              className="rounded-sm p-5 sm:p-6"
              style={{
                background: `linear-gradient(135deg, ${accent}0f 0%, ${accent}04 100%)`,
                border: `1px solid ${accent}2e`,
              }}
            >
              <p
                className="font-body mb-3"
                style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 600, lineHeight: 1.5, color: accentSoft }}
              >
                {item.q}
              </p>
              <p
                className="font-body"
                style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', fontWeight: 400, lineHeight: 1.75, color: 'rgba(232,224,213,0.72)' }}
              >
                {item.a}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Restart link */}
      <div className="mt-6 mb-16 text-center">
        <button
          onClick={onRestart}
          className="text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors text-[12px] tracking-[0.2em] uppercase font-body border-b border-[var(--line)] hover:border-[var(--ink-soft)] pb-1"
        >
          Refazer a jornada
        </button>
      </div>

    </motion.div>
  )
}

export default StepOffers
